import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp, TrendingFlat } from '@mui/icons-material';
import { ResponsiveLine, Serie } from '@nivo/line';
import { tokens } from '../../theme';
import { api } from '../../helpers/apiUtils';
import { determineAggregation } from '../../helpers/chartUtils';
import {
  AggregationMode,
  CompareEntry,
  CompareRange,
  GROUP_ORDER,
  GROUP_SHORT_LABELS,
  LEAD_METRICS,
  LeadsCompareResponse,
  METRIC_LABELS,
  METRIC_TOOLTIPS,
  MetricKey,
  Overview,
  OverviewCompareResponse,
  Portal,
  PortalGroup,
  TimeseriesRow,
  aggregationToBucket,
  calcDelta,
  extractCompareMetric,
  extractPreviousMetric,
  formatAggregationTick,
  formatMetricValue,
} from './shared';

// Hard cap to prevent quota-burning fan-out in compare mode (Critical-review
// finding #3: each selected portal triggers ~3 backend requests, so 10 portals
// = ~30 concurrent requests on first load).
const MAX_COMPARE_PORTALS = 10;
const COMPARE_PORTALS_WARN_AT = 6;

interface PortalComparisonViewProps {
  portals: Portal[];
  groupLabels: Record<string, string>;
  startStr: string;
  endStr: string;
  comparePortals: string[];
  setComparePortals: (keys: string[]) => void;
  activeMetric: MetricKey;
  setActiveMetric: (m: MetricKey) => void;
  previousRange: CompareRange | null;
  setPreviousRange: (r: CompareRange | null) => void;
}

// Metric columns for the comparison table (skip totalUsers because it's the
// identity metric on a traffic dashboard, keep it as the sort default).
const TABLE_METRICS: MetricKey[] = [
  'totalUsers',
  'newUsers',
  'sessions',
  'pageViews',
  'avgSessionDurationSeconds',
  'engagementRate',
  'leads',
  'leadsPerKUsers',
];

type SortDirection = 'asc' | 'desc';

const PortalComparisonView: React.FC<PortalComparisonViewProps> = ({
  portals,
  groupLabels,
  startStr,
  endStr,
  comparePortals,
  setComparePortals,
  activeMetric,
  setActiveMetric,
  previousRange,
  setPreviousRange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [compareData, setCompareData] = useState<Record<string, CompareEntry>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState<MetricKey>('totalUsers');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const [aggregationMode, setAggregationMode] = useState<AggregationMode>('auto');

  // Resolve "auto" to a concrete aggregation based on the current date range
  const effectiveAggregation = useMemo<'daily' | 'weekly' | 'monthly'>(() => {
    if (aggregationMode !== 'auto') return aggregationMode;
    if (!startStr || !endStr) return 'daily';
    return determineAggregation(new Date(startStr), new Date(endStr));
  }, [aggregationMode, startStr, endStr]);

  // Build grouped options for Autocomplete
  const autocompleteOptions = useMemo(() => {
    const ordered: Portal[] = [];
    for (const g of GROUP_ORDER) {
      const inGroup = portals
        .filter((p) => p.group === g)
        .sort((a, b) => {
          if (a.primary !== b.primary) return a.primary ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
      ordered.push(...inGroup);
    }
    return ordered;
  }, [portals]);

  const selectedPortalObjects = useMemo(
    () =>
      comparePortals
        .map((k) => portals.find((p) => p.key === k))
        .filter((p): p is Portal => Boolean(p)),
    [comparePortals, portals],
  );

  // ─── Data fetching ────────────────────────────────────────────────────

  useEffect(() => {
    if (!startStr || !endStr || comparePortals.length === 0) {
      setCompareData({});
      return;
    }
    // Wait until the portal registry has loaded so we can sanitize against it.
    if (portals.length === 0) return;

    // Critical-review finding #2: sanitize URL-derived comparePortals BEFORE
    // any fetches fire. Validate against the portal registry, dedupe, and
    // clamp to MAX_COMPARE_PORTALS. If the input was dirty, write back the
    // sanitized list and bail — the next render will run the fetch with a
    // clean list. The Autocomplete onChange enforces the same invariants for
    // user-driven changes; this protects the URL/deep-link entry point.
    const knownKeys = new Set(portals.map((p) => p.key));
    const seen = new Set<string>();
    const sanitized: string[] = [];
    for (const key of comparePortals) {
      if (!knownKeys.has(key)) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      sanitized.push(key);
      if (sanitized.length >= MAX_COMPARE_PORTALS) break;
    }
    const isDirty =
      sanitized.length !== comparePortals.length ||
      sanitized.some((k, i) => k !== comparePortals[i]);
    if (isDirty) {
      setComparePortals(sanitized);
      return; // bail; next render will fetch with the clean list
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const bucket = aggregationToBucket(effectiveAggregation);

    const fetchPortal = async (portal: Portal): Promise<[string, CompareEntry]> => {
      const params = {
        portal: portal.key,
        startDate: startStr,
        endDate: endStr,
      };
      const bucketedParams = { ...params, bucket };

      try {
        // Critical-review fix #3: gate on portal.hasLeads so the request only
        // fires when leads are expected. Any error from the leads endpoint
        // bubbles to the per-portal try/catch below and marks that portal as
        // errored — no silent "no lead data" state masking real failures.
        const leadsPromise = portal.hasLeads
          ? api.get('/api/ga4/leads', {
              params: { ...bucketedParams, compare: 1 },
            })
          : Promise.resolve(null);

        const [ovRes, tsRes, ldRes] = await Promise.all([
          api.get('/api/ga4/overview', {
            params: { ...params, compare: 1 },
          }),
          api.get('/api/ga4/timeseries', { params: bucketedParams }),
          leadsPromise,
        ]);

        const ovData = ovRes.data?.data as OverviewCompareResponse | null;
        const tsData = (tsRes.data?.data as TimeseriesRow[]) || [];

        let overview: Overview | null = null;
        let previousOverview: Overview | null = null;
        let prevRange: CompareRange | null = null;
        if (ovData && 'current' in ovData) {
          overview = ovData.current;
          previousOverview = ovData.previous;
          prevRange = ovData.previousRange;
        } else if (ovData) {
          overview = ovData as unknown as Overview;
        }

        let organicLeads: number | null = null;
        let previousOrganicLeads: number | null = null;
        let organicTimeseries: { date: string; leads: number }[] = [];
        if (ldRes?.data?.success && ldRes.data.data) {
          const ld = ldRes.data.data as LeadsCompareResponse;
          organicLeads = ld.current.organicLeads;
          previousOrganicLeads = ld.previous.organicLeads;
          organicTimeseries = ld.organicTimeseries || [];
        }

        return [
          portal.key,
          {
            portal,
            overview,
            previousOverview,
            previousRange: prevRange,
            timeseries: tsData,
            organicLeads,
            previousOrganicLeads,
            organicTimeseries,
          },
        ];
      } catch (err: any) {
        return [
          portal.key,
          {
            portal,
            overview: null,
            previousOverview: null,
            previousRange: null,
            timeseries: [],
            organicLeads: null,
            previousOrganicLeads: null,
            organicTimeseries: [],
            error: err?.message || 'Fehler beim Laden',
          },
        ];
      }
    };

    const toFetch = comparePortals
      .map((k) => portals.find((p) => p.key === k))
      .filter((p): p is Portal => Boolean(p));

    Promise.all(toFetch.map(fetchPortal))
      .then((results) => {
        if (cancelled) return;
        const nextData: Record<string, CompareEntry> = {};
        for (const [key, entry] of results) {
          nextData[key] = entry;
        }
        setCompareData(nextData);

        // Propagate previous-range subtitle from first successful entry
        const firstPrev = results.map(([, e]) => e.previousRange).find((pr) => pr !== null);
        if (firstPrev) {
          setPreviousRange(firstPrev);
        }

        const failed = results.filter(([, e]) => e.error).map(([k]) => k);
        if (failed.length > 0) {
          setError(`Fehler bei: ${failed.join(', ')}`);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Fehler beim Laden der Vergleichsdaten');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    startStr,
    endStr,
    comparePortals,
    portals,
    setComparePortals,
    setPreviousRange,
    effectiveAggregation,
  ]);

  // ─── Sort ─────────────────────────────────────────────────────────────

  const handleSort = (metric: MetricKey) => {
    if (sortBy === metric) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(metric);
      setSortDir('desc');
    }
  };

  const sortedEntries = useMemo(() => {
    const entries = selectedPortalObjects
      .map((p) => compareData[p.key])
      .filter((e): e is CompareEntry => Boolean(e));

    return [...entries].sort((a, b) => {
      const av = extractCompareMetric(a, sortBy);
      const bv = extractCompareMetric(b, sortBy);
      const an = av ?? -Infinity;
      const bn = bv ?? -Infinity;
      return sortDir === 'asc' ? an - bn : bn - an;
    });
  }, [selectedPortalObjects, compareData, sortBy, sortDir]);

  // ─── Chart series ─────────────────────────────────────────────────────

  const chartColors = [
    colors.greenAccent[500],
    colors.blueAccent[500],
    colors.redAccent[400],
    '#f59e0b',
    '#a855f7',
    '#14b8a6',
    '#ec4899',
  ];

  const isLeadMetric = LEAD_METRICS.includes(activeMetric);

  const chartSeries: Serie[] = useMemo(() => {
    // The backend returns rows already bucketed at the requested granularity
    // (GA4 dimension yearWeek/yearMonth for traffic, COUNT(DISTINCT) GROUP BY
    // for leads). We just plot them directly — no client-side aggregation,
    // no risk of inflating non-additive metrics like totalUsers.
    return selectedPortalObjects
      .map((portal) => {
        const entry = compareData[portal.key];
        if (!entry) return null;

        // Lead metrics: skip portals without lead data
        if (isLeadMetric && !portal.hasLeads) return null;

        let data: { x: string; y: number }[] = [];

        if (activeMetric === 'leads') {
          data = entry.organicTimeseries.map((r) => ({
            x: r.date,
            y: r.leads,
          }));
        } else if (activeMetric === 'leadsPerKUsers') {
          // Both inputs are bucket-aligned (same date keys), so the ratio
          // is mathematically correct per bucket.
          const usersMap = new Map(entry.timeseries.map((r) => [r.date, r.totalUsers]));
          data = entry.organicTimeseries.map((r) => {
            const u = usersMap.get(r.date);
            return {
              x: r.date,
              y: u && u > 0 ? Number(((r.leads / u) * 1000).toFixed(2)) : 0,
            };
          });
        } else {
          data = entry.timeseries.map((r) => ({
            x: r.date,
            y: (r as any)[activeMetric] || 0,
          }));
        }

        if (data.length === 0) return null;

        return {
          id: portal.name,
          data,
        } as Serie;
      })
      .filter((s): s is Serie => s !== null);
  }, [selectedPortalObjects, compareData, activeMetric, isLeadMetric]);

  // ─── Metric toggle availability ───────────────────────────────────────

  const anyHasLeads = selectedPortalObjects.some((p) => p.hasLeads);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <>
      {/* Portal multi-select */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
        <Autocomplete
          multiple
          options={autocompleteOptions}
          value={selectedPortalObjects}
          onChange={(_, v) => {
            // Critical-review fix #3: hard cap to prevent quota-burning bursts
            if (v.length > MAX_COMPARE_PORTALS) return;
            setComparePortals(v.map((p) => p.key));
          }}
          getOptionLabel={(p) => p.name}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          groupBy={(p) => groupLabels[p.group] || p.group}
          getOptionDisabled={(option) =>
            selectedPortalObjects.length >= MAX_COMPARE_PORTALS &&
            !selectedPortalObjects.some((p) => p.key === option.key)
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...chipProps}
                  label={option.name}
                  size="small"
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Portale zum Vergleichen"
              placeholder={
                selectedPortalObjects.length === 0 ? 'Mindestens 2 Portale wählen …' : ''
              }
            />
          )}
          sx={{ minWidth: 400 }}
        />
        {selectedPortalObjects.length >= COMPARE_PORTALS_WARN_AT && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: colors.grey[300] }}>
            Hinweis: Vergleich von {selectedPortalObjects.length} Portalen führt zu längeren
            Ladezeiten
            {selectedPortalObjects.length >= MAX_COMPARE_PORTALS
              ? ` (Maximum ${MAX_COMPARE_PORTALS} erreicht)`
              : ''}
            .
          </Typography>
        )}
      </Paper>

      {error && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: colors.redAccent[700],
            color: colors.grey[100],
          }}
        >
          <Typography>{error}</Typography>
        </Paper>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {comparePortals.length === 0 && !loading && (
        <Paper sx={{ p: 4, backgroundColor: colors.primary[400] }}>
          <Typography color={colors.grey[100]} align="center">
            Wähle mindestens ein Portal aus (idealerweise 2 oder mehr), um Portale zu vergleichen.
          </Typography>
        </Paper>
      )}

      {/* Comparison table */}
      {sortedEntries.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
          {previousRange && (
            <Typography variant="caption" sx={{ color: colors.grey[300], mb: 1, display: 'block' }}>
              Vergleich zum Vorzeitraum: {previousRange.startDate} – {previousRange.endDate}
            </Typography>
          )}
          <TableContainer sx={{ backgroundColor: '#ffffff', maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      minWidth: 220,
                      boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
                    }}
                  >
                    Portal
                  </TableCell>
                  {TABLE_METRICS.map((m) => (
                    <TableCell
                      key={m}
                      align="right"
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Tooltip
                        title={METRIC_TOOLTIPS[m]}
                        arrow
                        placement="top"
                        enterDelay={300}
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '0.75rem',
                              lineHeight: 1.45,
                              maxWidth: 280,
                              backgroundColor: colors.primary[500],
                              color: colors.grey[100],
                              px: 1.25,
                              py: 1,
                            },
                          },
                          arrow: { sx: { color: colors.primary[500] } },
                        }}
                      >
                        <TableSortLabel
                          active={sortBy === m}
                          direction={sortBy === m ? sortDir : 'desc'}
                          onClick={() => handleSort(m)}
                        >
                          {METRIC_LABELS[m]}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEntries.map((entry) => (
                  <TableRow
                    key={entry.portal.key}
                    hover
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      '&:hover .sticky-portal-cell': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <TableCell
                      className="sticky-portal-cell"
                      sx={{
                        color: 'black',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: '#ffffff',
                        minWidth: 220,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <span style={{ fontWeight: 500 }}>{entry.portal.name}</span>
                        <Chip
                          label={
                            GROUP_SHORT_LABELS[entry.portal.group as PortalGroup] ||
                            entry.portal.group
                          }
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            backgroundColor: '#e0e0e0',
                            color: 'black',
                          }}
                        />
                      </Box>
                    </TableCell>
                    {TABLE_METRICS.map((m) => {
                      const val = extractCompareMetric(entry, m);
                      const prev = extractPreviousMetric(entry, m);
                      const delta = val !== null && prev !== null ? calcDelta(val, prev) : null;

                      const isUp = delta !== null && delta > 0;
                      const isDown = delta !== null && delta < 0;
                      const isFlat = delta !== null && delta === 0;
                      const deltaColor = isUp
                        ? colors.greenAccent[600]
                        : isDown
                          ? colors.redAccent[500]
                          : 'black';

                      return (
                        <TableCell
                          key={m}
                          align="right"
                          sx={{ color: 'black', whiteSpace: 'nowrap' }}
                        >
                          <div>{formatMetricValue(m, val)}</div>
                          {delta !== null && (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              sx={{
                                fontSize: '0.7rem',
                                color: deltaColor,
                                fontWeight: 500,
                              }}
                            >
                              {isUp && <ArrowDropUp sx={{ fontSize: 14, color: deltaColor }} />}
                              {isDown && <ArrowDropDown sx={{ fontSize: 14, color: deltaColor }} />}
                              {isFlat && (
                                <TrendingFlat
                                  sx={{
                                    fontSize: 12,
                                    color: deltaColor,
                                    mr: 0.25,
                                  }}
                                />
                              )}
                              {delta > 0 ? '+' : ''}
                              {delta.toFixed(1)} %
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Multi-line chart */}
      {sortedEntries.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
          <Typography variant="h5" color={colors.grey[100]} mb={1}>
            Verlauf – {METRIC_LABELS[activeMetric]}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <ToggleButtonGroup
                value={aggregationMode}
                exclusive
                size="small"
                onChange={(_, v) => v && setAggregationMode(v)}
              >
                <ToggleButton value="auto">
                  Auto
                  {aggregationMode === 'auto' && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>
                      (
                      {effectiveAggregation === 'daily'
                        ? 'tägl.'
                        : effectiveAggregation === 'weekly'
                          ? 'wö.'
                          : 'mo.'}
                      )
                    </Typography>
                  )}
                </ToggleButton>
                <ToggleButton value="daily">Täglich</ToggleButton>
                <ToggleButton value="weekly">Wöchentlich</ToggleButton>
                <ToggleButton value="monthly">Monatlich</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                value={activeMetric}
                exclusive
                size="small"
                onChange={(_, v) => v && setActiveMetric(v)}
              >
                {(Object.keys(METRIC_LABELS) as MetricKey[])
                  .filter((k) => !LEAD_METRICS.includes(k) || anyHasLeads)
                  .map((k) => (
                    <ToggleButton key={k} value={k}>
                      {METRIC_LABELS[k]}
                    </ToggleButton>
                  ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
          <Box height={440}>
            {chartSeries.length > 0 && (
              <ResponsiveLine
                data={chartSeries}
                theme={{
                  axis: {
                    domain: { line: { stroke: colors.grey[100] } },
                    legend: { text: { fill: colors.grey[100] } },
                    ticks: {
                      line: { stroke: colors.grey[100], strokeWidth: 1 },
                      text: { fill: colors.grey[100] },
                    },
                  },
                  legends: { text: { fill: colors.grey[100] } },
                  tooltip: { container: { color: colors.primary[500] } },
                }}
                colors={chartColors.slice(0, chartSeries.length)}
                margin={{ top: 20, right: 160, bottom: 60, left: 80 }}
                xScale={{ type: 'point' }}
                yScale={{
                  type: 'linear',
                  min: 0,
                  max: 'auto',
                  stacked: false,
                  reverse: false,
                }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: 'Datum',
                  legendOffset: 50,
                  legendPosition: 'middle',
                  format: (v) => formatAggregationTick(String(v), effectiveAggregation),
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: METRIC_LABELS[activeMetric],
                  legendOffset: -70,
                  legendPosition: 'middle',
                  format: (v) =>
                    activeMetric === 'engagementRate'
                      ? `${(Number(v) * 100).toFixed(0)}%`
                      : Number(v).toLocaleString('de-DE'),
                }}
                pointSize={4}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                useMesh={true}
                enableSlices="x"
                legends={[
                  {
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 150,
                    translateY: 0,
                    itemsSpacing: 4,
                    itemDirection: 'left-to-right',
                    itemWidth: 140,
                    itemHeight: 20,
                    itemOpacity: 0.85,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(255, 255, 255, .05)',
                          itemOpacity: 1,
                        },
                      },
                    ],
                  },
                ]}
              />
            )}
          </Box>
        </Paper>
      )}
    </>
  );
};

export default PortalComparisonView;
